import {
    faCircleExclamation,
    faCode,
    faDatabase,
    faFileCircleExclamation,
    faLink,
    faPersonCircleExclamation,
    faPlugCircleExclamation,
    faServer,
    faStopwatch,
    faTriangleExclamation,
    faUserCheck,
    faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { QueueLifecycleStatus, TaskFailureCategory } from "../../app/enums";

export type FailureDisplayContext = "Task" | "Extract" | "Transform";

export interface LifecycleDisplayModel {
    label: "Waiting" | "In progress" | "Completed" | "Failed" | "Unknown";
    styleKey: "queue-notexecuted" | "queue-inprogress" | "queue-completed" | "queue-failed" | "queue-unknown";
    accessibleLabel: string;
    isFailed: boolean;
}

export interface CategoryIndicatorDisplayModel {
    icon: IconDefinition;
    label: string;
    tooltip: string;
    accessibleLabel: string;
    isKnown: boolean;
}

export interface FailureDetailsDisplayModel {
    categoryLabel: string;
    message: string;
    failedAt: string;
    attempt: string;
    correlationReference: string;
}

export interface RequeueProvenanceDisplayModel {
    hasProvenance: boolean;
    accessibleLabel: string;
    tooltip: string;
}

const unavailable = "Unavailable";
const unknownCategory: CategoryIndicatorDisplayModel = {
    icon: faTriangleExclamation,
    label: "Unknown failure category",
    tooltip: "An unsupported failure category was supplied.",
    accessibleLabel: "Unknown failure category",
    isKnown: false
};

const categories: Record<FailureDisplayContext, Record<string, CategoryIndicatorDisplayModel>> = {
    Task: {
        [TaskFailureCategory.Validation]: { icon: faUserCheck, label: "Validation", tooltip: "Task validation failure", accessibleLabel: "Validation failure", isKnown: true },
        [TaskFailureCategory.ServiceBus]: { icon: faLink, label: "Service Bus", tooltip: "Task Service Bus failure", accessibleLabel: "Service Bus failure", isKnown: true },
        [TaskFailureCategory.Execution]: { icon: faCircleExclamation, label: "Execution", tooltip: "Task execution failure", accessibleLabel: "Execution failure", isKnown: true }
    },
    Extract: {
        HistoricalMigration: { icon: faDatabase, label: "Historical migration", tooltip: "Extract failure recorded before failure categories were introduced", accessibleLabel: "Extract historical migration failure", isKnown: true },
        Validation: { icon: faUserCheck, label: "Validation", tooltip: "Extract validation failure", accessibleLabel: "Extract validation failure", isKnown: true },
        ExtractUploadServer: { icon: faServer, label: "People upload server", tooltip: "Extract people upload server failure", accessibleLabel: "Extract people upload server failure", isKnown: true },
        ProviderApi: { icon: faPlugCircleExclamation, label: "Provider API", tooltip: "Extract provider API failure", accessibleLabel: "Extract provider API failure", isKnown: true },
        Timeout: { icon: faStopwatch, label: "Timeout", tooltip: "Extract timeout failure", accessibleLabel: "Extract timeout failure", isKnown: true },
        MalformedResponse: { icon: faFileCircleExclamation, label: "Malformed response", tooltip: "Extract malformed response failure", accessibleLabel: "Extract malformed response failure", isKnown: true },
        Mapping: { icon: faDatabase, label: "Mapping", tooltip: "Extract mapping failure", accessibleLabel: "Extract mapping failure", isKnown: true },
        UnsupportedProvider: { icon: faPlugCircleExclamation, label: "Unsupported provider", tooltip: "Extract unsupported provider failure", accessibleLabel: "Extract unsupported provider failure", isKnown: true },
        Runtime: { icon: faCircleExclamation, label: "Runtime", tooltip: "Extract runtime failure", accessibleLabel: "Extract runtime failure", isKnown: true }
    },
    Transform: {
        HistoricalMigration: { icon: faDatabase, label: "Historical migration", tooltip: "Transform failure recorded before failure categories were introduced", accessibleLabel: "Transform historical migration failure", isKnown: true },
        TransformScript: { icon: faCode, label: "Transform script", tooltip: "Transform script failure", accessibleLabel: "Transform script failure", isKnown: true },
        IdentityIntegrity: { icon: faPersonCircleExclamation, label: "Identity integrity", tooltip: "Transform identity integrity failure", accessibleLabel: "Transform identity integrity failure", isKnown: true },
        Api: { icon: faPlugCircleExclamation, label: "API", tooltip: "Transform API failure", accessibleLabel: "Transform API failure", isKnown: true },
        Timeout: { icon: faStopwatch, label: "Timeout", tooltip: "Transform timeout failure", accessibleLabel: "Transform timeout failure", isKnown: true },
        Storage: { icon: faDatabase, label: "Storage", tooltip: "Transform storage failure", accessibleLabel: "Transform storage failure", isKnown: true },
        DataTransformation: { icon: faDatabase, label: "Data transformation", tooltip: "Transform data transformation failure", accessibleLabel: "Transform data transformation failure", isKnown: true },
        Configuration: { icon: faWandMagicSparkles, label: "Configuration", tooltip: "Transform configuration failure", accessibleLabel: "Transform configuration failure", isKnown: true },
        ProviderState: { icon: faPlugCircleExclamation, label: "Provider state", tooltip: "Transform provider state failure", accessibleLabel: "Transform provider state failure", isKnown: true },
        Runtime: { icon: faCircleExclamation, label: "Runtime", tooltip: "Transform runtime failure", accessibleLabel: "Transform runtime failure", isKnown: true }
    }
};

export function getLifecycleDisplay(lifecycleStatus: number | null | undefined): LifecycleDisplayModel {
    switch (lifecycleStatus) {
        case QueueLifecycleStatus.Waiting:
            return { label: "Waiting", styleKey: "queue-notexecuted", accessibleLabel: "Queue status: Waiting", isFailed: false };
        case QueueLifecycleStatus.InProgress:
            return { label: "In progress", styleKey: "queue-inprogress", accessibleLabel: "Queue status: In progress", isFailed: false };
        case QueueLifecycleStatus.Completed:
            return { label: "Completed", styleKey: "queue-completed", accessibleLabel: "Queue status: Completed", isFailed: false };
        case QueueLifecycleStatus.Failed:
            return { label: "Failed", styleKey: "queue-failed", accessibleLabel: "Queue status: Failed", isFailed: true };
        default:
            return { label: "Unknown", styleKey: "queue-unknown", accessibleLabel: "Queue status: Unknown", isFailed: false };
    }
}

export function getQueueStatusDisplay(status: number | null | undefined): LifecycleDisplayModel {
    switch (status) {
        case 1:
            return getLifecycleDisplay(QueueLifecycleStatus.Waiting);
        case 2:
            return getLifecycleDisplay(QueueLifecycleStatus.Failed);
        case 3:
            return getLifecycleDisplay(QueueLifecycleStatus.Completed);
        case 4:
            return getLifecycleDisplay(QueueLifecycleStatus.InProgress);
        default:
            return getLifecycleDisplay(undefined);
    }
}

export function getCategoryIndicator(context: FailureDisplayContext, category: string | number | null | undefined): CategoryIndicatorDisplayModel | null {
    if (category === null || category === undefined || category === "") {
        return null;
    }

    return categories[context][String(category)] ?? unknownCategory;
}

export function projectFailureDetails(
    context: Exclude<FailureDisplayContext, "Task">,
    category: string | null | undefined,
    failureMessage: string | null | undefined,
    failedAt: string | null | undefined,
    attemptNumber: number | null | undefined,
    correlationId: string | null | undefined
): FailureDetailsDisplayModel {
    const categoryIndicator = getCategoryIndicator(context, category);
    const date = failedAt ? new Date(failedAt) : null;

    return {
        categoryLabel: categoryIndicator?.label ?? unavailable,
        message: failureMessage || unavailable,
        failedAt: date && !Number.isNaN(date.valueOf()) ? date.toISOString() : unavailable,
        attempt: attemptNumber && attemptNumber > 0 ? String(attemptNumber) : unavailable,
        correlationReference: correlationId || unavailable
    };
}

export function getRequeueProvenance(previousQueueId: string | null | undefined, requeuedByUserName: string | null | undefined): RequeueProvenanceDisplayModel {
    const values = [
        previousQueueId ? `Previous queue: ${previousQueueId}` : null,
        requeuedByUserName ? `Requeued by: ${requeuedByUserName}` : null
    ].filter((value): value is string => value !== null);

    return {
        hasProvenance: values.length > 0,
        accessibleLabel: values.length > 0 ? "Requeue history available" : "No requeue history",
        tooltip: values.length > 0 ? values.join("\n") : ""
    };
}